import * as React from "react"
import { ConnectionStatusType, ConnectionType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import Cookies from "js-cookie"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { CommodityRate } from "@/types/meter"
import { addCommodityRate, editCommodityRate } from "@/lib/api/commodity-rates"
import { addConnectionStatus } from "@/lib/api/connection-status"
import { getConnectionType } from "@/lib/api/meter"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Header } from "@/components/header"

import { ComboboxSearch } from "../combobox-search"
import { Icons } from "../icons"
import { Card } from "../ui/card"
import { Input } from "../ui/input"
import { Textarea } from "../ui/textarea"

const formSchema = z.object({
  rate_type_id: z.coerce.number().min(1, "Please select a connection type"),
  min_volume: z.coerce.number().min(0, "Minimum volume is required"),
  max_volume: z.coerce.number().min(0, "Maximum volume is required"),

  charge_per_cubic_meter: z
    .string()
    .min(1, "Charge per cubic meter is required")
    .transform((val) => Number(val)),

  unlimited_tier: z.boolean().optional(),
})

export default function CommodityRateForm({
  editData,
  setShowAddCommodityRateModal,
}: {
  editData?: CommodityRate
  setShowAddCommodityRateModal?: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const {
    data: connetionType,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["connection_type"],
    queryFn: getConnectionType,
  })

  const { toast } = useToast()
  const queryClient = useQueryClient()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      rate_type_id: editData?.rate_type_id || 0,
      min_volume: editData?.min_volume || 0,
      max_volume: editData?.max_volume || 0,
      charge_per_cubic_meter: Number(editData?.charge_per_cubic_meter) || 0,
      unlimited_tier: editData?.unlimited_tier || false,
    },
  })

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      if (editData) {
        return await editCommodityRate(editData.id, data)
      }
      return await addCommodityRate(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["commodityRates"] })
      editData
        ? toast({ description: "Commodity Rate updated successfully." })
        : toast({ description: "Commodity Rate added successfully." })

      setShowAddCommodityRateModal?.(false)
    },
    onError: (error: any) => {
      toast({
        title: "Something went wrong",
        description: error?.response?.data?.error || "Please try again.",
        variant: "destructive",
      })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    mutation.mutate(values)
  }

  const handleError = (errors: any) => {
    console.error("Form validation errors:", errors)

    // Optional: show toast
    toast({
      title: "Form validation failed",
      description: "Please check required fields.",
      variant: "destructive",
    })
  }

  const connectionTypes: Array<{
    value: string
    label: string
    id?: string | number
  }> =
    (connetionType as ConnectionType[]) // Ensure data is an array
      ?.map((item) => ({
        value: item.type_name,
        label: item.type_name,
        id: item.id,
      }))
      .filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.value === item.value), // Ensure unique values
      ) ?? []

  return (
    <div className="px-2 w-full">
      <Header
        subroutes={[
          {
            route: "/meter-connection-settings/connection-status",
            label: "Connection Status",
          },
        ]}
      >
        {`${editData ? "Edit" : "Add"} Connection Status`}
      </Header>
      <DropdownMenuSeparator className="mt-4" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit, handleError)}
          className="grid grid-cols-1 gap-4 lg:container md:max-w-[1000px]"
        >
          <Card className="mb-3 space-y-4 border-0">
            {/* Connection Type */}
            <FormField
              control={form.control}
              name="rate_type_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>
                    Connection Type <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <ComboboxSearch
                      field={{
                        ...field,
                      }}
                      options={connectionTypes}
                      placeholder="Select a connection type"
                      idAsValue
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Min Volume */}
            <FormField
              control={form.control}
              name="min_volume"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Min Volume <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Max Volume */}
            <FormField
              control={form.control}
              name="max_volume"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Max Volume <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Price */}
            <FormField
              control={form.control}
              name="charge_per_cubic_meter"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Price per m³ <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="number" step="0.01" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Card>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAddCommodityRateModal?.(false)}
            >
              Cancel
            </Button>

            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && (
                <Icons.loader className="mr-2 animate-spin" />
              )}
              {editData ? "Update" : "Add"} Commodity Rate
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
